import React from 'react'
import styles from './Button.module.scss'

export type ButtonProps = {
  buttonType?: 'DEFAULT' | 'SMALL' | 'XS'
  kind?: 'primary' | 'secondary' | 'danger' | 'warning' | 'outline'
  children: React.ReactNode
  onClick?: () => void
  link?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

const DEFAULT_PROPS: { buttonType: 'DEFAULT' | 'SMALL' | 'XS'; kind: 'primary' | 'secondary' | 'danger' | 'warning' | 'outline' } = {
  buttonType: 'DEFAULT',
  kind: 'primary',
};

const kindStyles = {
  primary: {
    border: 'border-1 border-member-green-100',
    bg: 'bg-member-green-100',
    text: 'text-member-gray-0',
    hover: 'hover:border-1 hover:border-member-green-100 hover:bg-member-gray-0 hover:text-member-green-100',
    focus: 'focus:border-1 focus:border-member-green-100 focus:bg-member-gray-0 focus:text-member-green-100',
  },
  secondary: {
    border: 'border-1 border-member-light-green-100',
    bg: 'bg-member-light-green-100',
    text: 'text-member-green-100',
    hover: 'hover:border-1 hover:border-member-green-100 hover:bg-member-gray-0',
    focus: 'focus:border-1 focus:border-member-green-100 focus:bg-member-gray-0',
  },
  danger: {
    border: 'border-1 border-member-red-100',
    bg: 'bg-member-red-100',
    text: 'text-member-gray-0',
    hover: 'hover:border-1 hover:border-member-red-100 hover:bg-member-gray-0 hover:text-member-red-100',
    focus: 'focus:border-1 focus:border-member-red-100 focus:bg-member-gray-0 focus:text-member-red-100',
  },
  warning: {
    border: 'border-1 border-member-orange-100',
    bg: 'bg-member-orange-100',
    text: 'text-member-gray-0',
    hover: 'hover:border-1 hover:border-member-orange-100 hover:bg-member-gray-0 hover:text-member-orange-100',
    focus: 'focus:border-1 focus:border-member-orange-100 focus:bg-member-gray-0 focus:text-member-orange-100',
  },
  outline: {
    border: 'border-1 border-member-green-100',
    bg: 'bg-member-gray-0',
    text: 'text-member-green-100',
    hover: 'hover:border-1 hover:border-member-green-100 hover:bg-member-green-100 hover:text-member-gray-0',
    focus: 'focus:border-1 focus:border-member-green-100 focus:bg-member-green-100 focus:text-member-gray-0',
  },
}

const generateKindStyles = (kind: keyof typeof kindStyles) => {
  const styles = kindStyles[kind] || kindStyles.primary
  return `${styles.border} ${styles.bg} ${styles.text} ${styles.hover} ${styles.focus}`
}

export const StyledButton: React.FC<ButtonProps> = ({
  buttonType = DEFAULT_PROPS.buttonType,
  kind = DEFAULT_PROPS.kind,
  children,
  className = '',
  disabled = false,
  onClick,
  link = false,
}) => {
  const baseButtonStyles = `flex justify-center items-center rounded text-center font-bold w-max`
  const sizeStyles = (() => {
    switch (buttonType) {
      case 'SMALL':
        return 'px-XL py-S text-S'
      case 'XS':
        return 'px-M py-XS text-XS'
      default:
        return 'px-2XL py-M text-M'
    }
  })()

  const kindClass = generateKindStyles(kind)

  return (
    link ? (
      <span
        className={`${styles[kind]} ${styles.link} ${baseButtonStyles} ${sizeStyles} ${kindClass} ${className}`}
        style={{ letterSpacing: '.02em', cursor: 'pointer', wordBreak: 'keep-all' }}
        aria-disabled={disabled}
        onClick={onClick}
      >
        {children}
      </span>
    ) : (
    <button
      className={`${baseButtonStyles} ${sizeStyles} ${kindClass} ${className}`}
      style={{ letterSpacing: '.02em', cursor: 'pointer', wordBreak: 'keep-all' }}
      disabled={disabled}
      aria-disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
    )
  )
}
